import type { BeadTechnique } from '@bransoletki/shared';

interface Coord {
  readonly row: number;
  readonly col: number;
}

/**
 * Get neighbor coordinates for a bead based on technique.
 * Each technique has different adjacency rules.
 */
function getNeighbors(
  row: number,
  col: number,
  technique: BeadTechnique,
  width: number,
  height: number,
): readonly Coord[] {
  const neighbors: Coord[] = [];

  const addIfValid = (r: number, c: number) => {
    if (r >= 0 && r < height && c >= 0 && c < width) {
      neighbors.push({ row: r, col: c });
    }
  };

  switch (technique) {
    case 'loom':
      // Rectangular grid: 4-connected (up, down, left, right)
      addIfValid(row - 1, col);
      addIfValid(row + 1, col);
      addIfValid(row, col - 1);
      addIfValid(row, col + 1);
      break;

    case 'peyote': {
      // Offset grid: even rows flush left, odd rows offset right
      // Each bead has up to 6 neighbors (hexagonal-like)
      const isOddRow = row % 2 === 1;

      // Same row neighbors
      addIfValid(row, col - 1);
      addIfValid(row, col + 1);

      // Row above
      if (isOddRow) {
        addIfValid(row - 1, col);
        addIfValid(row - 1, col + 1);
      } else {
        addIfValid(row - 1, col - 1);
        addIfValid(row - 1, col);
      }

      // Row below
      if (isOddRow) {
        addIfValid(row + 1, col);
        addIfValid(row + 1, col + 1);
      } else {
        addIfValid(row + 1, col - 1);
        addIfValid(row + 1, col);
      }
      break;
    }

    case 'brick': {
      // Same offset pattern as peyote (both use staggered rows)
      const isOddRow = row % 2 === 1;

      addIfValid(row, col - 1);
      addIfValid(row, col + 1);

      if (isOddRow) {
        addIfValid(row - 1, col);
        addIfValid(row - 1, col + 1);
        addIfValid(row + 1, col);
        addIfValid(row + 1, col + 1);
      } else {
        addIfValid(row - 1, col - 1);
        addIfValid(row - 1, col);
        addIfValid(row + 1, col - 1);
        addIfValid(row + 1, col);
      }
      break;
    }
  }

  return neighbors;
}

/**
 * Flood fill from a starting bead, replacing `targetColor` with `fillColor`.
 * Returns a new grid (immutable).
 */
export function floodFill(
  grid: readonly (readonly number[])[],
  startRow: number,
  startCol: number,
  fillColor: number,
  technique: BeadTechnique,
): readonly (readonly number[])[] {
  const height = grid.length;
  if (height === 0) return grid;
  const firstRow = grid[0];
  if (!firstRow) return grid;
  const width = firstRow.length;

  const targetRow = grid[startRow];
  if (!targetRow) return grid;
  const targetColor = targetRow[startCol];
  if (targetColor === undefined || targetColor === fillColor) return grid;

  // Copy grid for mutation
  const newGrid: number[][] = grid.map((row) => [...row]);

  // BFS
  const queue: Coord[] = [{ row: startRow, col: startCol }];
  const visited = new Set<string>();
  visited.add(`${startRow},${startCol}`);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentRow = newGrid[current.row];
    if (!currentRow) continue;

    currentRow[current.col] = fillColor;

    for (const neighbor of getNeighbors(current.row, current.col, technique, width, height)) {
      const key = `${neighbor.row},${neighbor.col}`;
      if (visited.has(key)) continue;
      visited.add(key);

      const nRow = newGrid[neighbor.row];
      if (nRow && nRow[neighbor.col] === targetColor) {
        queue.push(neighbor);
      }
    }
  }

  return newGrid;
}
